import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import BaseModal from 'src/components/BaseModal';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import Select from 'src/components/Select';
import Checkbox from 'src/components/Checkbox';
import Loader from 'src/components/Loader';
import { CaretDown, CaretRight } from 'src/common/icons';
import useModal from 'src/hooks/useModal';
import useRoleManager from 'src/hooks/useRoleManager';
import { useAlerts } from 'src/common/AlertManager';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { queryNames } from 'src/api/utils/queryNames';
import { getJobStatus } from 'src/api/functions/assetsApi';
import {
  downloadPrintExport,
  savePrintExport,
  PrintExportParams,
  PrintExportDisposition,
} from 'src/api/functions/addonsApi';
import messages from '../messages';

type OutputMode = 'download' | PrintExportDisposition;

type PrintExportDialogProps = {
  assetId: string;
  assetName: string;
};

const FORMAT_OPTIONS = [
  { label: 'CMYK TIFF (lossless)', value: 'tiff' },
  { label: 'CMYK JPEG', value: 'jpeg' },
  { label: 'Print PDF', value: 'pdf' },
  { label: 'PDF/X-1a', value: 'pdfx' },
];

const CMYK_PROFILE_OPTIONS = [
  { label: 'US Web Coated (SWOP)', value: 'USWebCoatedSWOP' },
  { label: 'Coated FOGRA39', value: 'CoatedFOGRA39' },
  { label: 'Coated FOGRA27', value: 'CoatedFOGRA27' },
  { label: 'Uncoated FOGRA29', value: 'UncoatedFOGRA29' },
  { label: 'Web Coated FOGRA28', value: 'WebCoatedFOGRA28' },
  { label: 'US Web Uncoated', value: 'USWebUncoated' },
  { label: 'US Sheetfed Coated', value: 'USSheetfedCoated' },
  { label: 'US Sheetfed Uncoated', value: 'USSheetfedUncoated' },
  { label: 'Japan Color 2001 Coated', value: 'JapanColor2001Coated' },
  { label: 'Japan Color 2001 Uncoated', value: 'JapanColor2001Uncoated' },
];

const RENDERING_INTENT_OPTIONS = [
  { label: 'Perceptual', value: 'perceptual' },
  { label: 'Relative colorimetric', value: 'relative_colorimetric' },
  { label: 'Saturation', value: 'saturation' },
  { label: 'Absolute colorimetric', value: 'absolute_colorimetric' },
];

const TARGET_SIZE_OPTIONS = [
  { label: 'Original (no resize)', value: '' },
  { label: 'A6', value: 'A6' },
  { label: 'A5', value: 'A5' },
  { label: 'A4', value: 'A4' },
  { label: 'A3', value: 'A3' },
  { label: 'A2', value: 'A2' },
  { label: 'Letter', value: 'letter' },
  { label: 'Legal', value: 'legal' },
  { label: 'Tabloid', value: 'tabloid' },
];

// Poll the save job briefly; if it doesn't finish in this window we hand off
// to the background (the job + completion notification take over).
const JOB_POLL_INTERVAL_MS = 2000;
const JOB_POLL_MAX_ATTEMPTS = 3; // ~6s

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 };
const rowStyle: React.CSSProperties = { marginBottom: 14 };
const hintStyle: React.CSSProperties = { display: 'block', fontSize: 12, color: '#888', margin: '0 0 6px', lineHeight: 1.4 };
const toggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
  color: '#2745c4',
};
const defaultsNoteStyle: React.CSSProperties = { margin: '6px 0 12px', fontSize: 12, color: '#777', lineHeight: 1.5 };
const caretStyle: React.CSSProperties = { width: 12, height: 12 };

export const PrintExportDialog: React.FC<PrintExportDialogProps> = ({ assetId, assetName }) => {
  const { formatMessage } = useIntl();
  const { closeModal } = useModal();
  const { showSuccess, showAlert, showInfo } = useAlerts();
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { isAdmin } = useRoleManager();

  const [format, setFormat] = useState('tiff');
  const [targetSize, setTargetSize] = useState('');
  const [dpi, setDpi] = useState('300');
  const [cmykProfile, setCmykProfile] = useState('USWebCoatedSWOP');
  const [renderingIntent, setRenderingIntent] = useState('relative_colorimetric');
  const [allowUpscale, setAllowUpscale] = useState(false);

  const [outputMode, setOutputMode] = useState<OutputMode>('download');
  const [conversionName, setConversionName] = useState('print_ready');
  const [inheritMetadata, setInheritMetadata] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [notifying, setNotifying] = useState(false);
  // Print parameters stay folded; sensible defaults apply unless opened.
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const buildParams = (): PrintExportParams => {
    const parsedDpi = parseInt(dpi, 10);
    return {
      format,
      cmyk_profile: cmykProfile,
      rendering_intent: renderingIntent,
      dpi: Number.isFinite(parsedDpi) ? parsedDpi : 300,
      target_size: targetSize || undefined,
      allow_upscale: targetSize ? allowUpscale : false,
    };
  };

  const mapErrorMessage = (error: any): string => {
    const status = error?.status;
    const body = error?.data || {};
    // The print-export handler wraps its fields in `data` ({"data": {"error":
    // "quota_exhausted", ...}}); unwrap so the 402 branches can match.
    const data = body?.data && typeof body.data === 'object' ? body.data : body;
    if (status === 403) return formatMessage(messages.printExportNotEnabled);
    if (status === 402) {
      if (data?.error === 'insufficient_credits') {
        return formatMessage(messages.printExportInsufficientCredits, {
          required: data.required ?? '?',
          balance: data.balance ?? '?',
        });
      }
      // Past the monthly allowance an export costs credits; this 402 means
      // the wallet can't cover it. A trial never pays, so its 402 carries no
      // `overage_credits` and the allowance itself is the limit.
      if (data?.overage_credits != null) {
        return formatMessage(
          isAdmin ? messages.printExportOverageShortAdmin : messages.printExportOverageShortMember,
          { credits: data.overage_credits, balance: data.balance ?? 0 },
        );
      }
      return formatMessage(messages.printExportQuotaExhausted);
    }
    // Backend errors (e.g. 400 "image too small for the requested print
    // size") carry a human-readable `message` — surface it as-is.
    if (typeof data?.message === 'string' && data.message) return data.message;
    return formatMessage(messages.printExportFailed);
  };

  const handleDownload = async () => {
    setSubmitting(true);
    try {
      const res = await downloadPrintExport(assetId, assetName, buildParams(), accessToken);
      if (!mountedRef.current) return;
      if (res.ok) {
        showInfo(formatMessage(messages.printExportDownloadStarted));
        closeModal();
      } else {
        showAlert(mapErrorMessage(res.error));
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const pollSaveJob = async (
    jobId: string,
  ): Promise<{ outcome: 'done' | 'error' | 'timeout'; message?: string }> => {
    for (let attempt = 0; attempt < JOB_POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise((r) => setTimeout(r, JOB_POLL_INTERVAL_MS));
      if (!mountedRef.current) return { outcome: 'timeout' };
      const job: any = await getJobStatus(jobId, accessToken);
      const status = job?.status;
      if (status === 'COMPLETED') return { outcome: 'done' };
      if (status === 'ERROR') {
        return { outcome: 'error', message: typeof job?.message === 'string' ? job.message : undefined };
      }
    }
    return { outcome: 'timeout' };
  };

  const handleSave = async (disposition: PrintExportDisposition) => {
    setSubmitting(true);
    try {
      const res = await savePrintExport(
        assetId,
        buildParams(),
        disposition,
        { conversion_name: conversionName, inherit_metadata: inheritMetadata },
        accessToken,
      );
      if (!mountedRef.current) return;
      if (!res.ok || !res.jobId) {
        showAlert(mapErrorMessage(res.error));
        return;
      }
      const { outcome, message } = await pollSaveJob(res.jobId);
      if (!mountedRef.current) return;
      if (outcome === 'done') {
        queryClient.invalidateQueries([queryNames.asset, assetId]);
        showSuccess(
          disposition === 'add_conversion'
            ? formatMessage(messages.printExportSavedConversion, { conversionName })
            : formatMessage(messages.printExportSavedNewAsset),
        );
        closeModal();
      } else if (outcome === 'error') {
        showAlert(message || formatMessage(messages.printExportFailed));
      } else {
        // Still running — hand off to the background job + notification.
        setNotifying(true);
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (outputMode === 'download') return handleDownload();
    return handleSave(outputMode);
  };

  const primaryLabel =
    outputMode === 'download'
      ? formatMessage(messages.downloadContent)
      : formatMessage(messages.exportForPrint);

  return (
    <BaseModal containerStyle={{ width: 'min(560px, 92vw)', maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={{ padding: 4 }}>
        <h3 style={{ margin: '0 0 4px' }}>{formatMessage(messages.exportForPrint)}</h3>
        <p style={{ margin: '0 0 18px', color: '#666', fontSize: 13 }}>
          Generate a professional print file (CMYK / 300 DPI). Print files are not previewable in the browser.
        </p>

        {notifying ? (
          <div style={{ padding: '12px 0' }}>
            <p style={{ fontSize: 14 }}>{formatMessage(messages.printExportNotify)}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button mode="primary" size="small" onClick={() => closeModal()}>
                {formatMessage(messages.cancelEditImage)}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div style={rowStyle}>
              <span style={labelStyle}>Output</span>
              {(
                [
                  { value: 'download', label: 'Download the print file' },
                  { value: 'add_conversion', label: 'Save as a conversion on this asset' },
                  { value: 'add_new_asset', label: 'Save as a new asset' },
                ] as { value: OutputMode; label: string }[]
              ).map((opt) => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input
                    type="radio"
                    name="print-export-output"
                    checked={outputMode === opt.value}
                    onChange={() => setOutputMode(opt.value)}
                  />
                  <span style={{ fontSize: 14 }}>{opt.label}</span>
                </label>
              ))}
            </div>

            {outputMode === 'add_conversion' && (
              <div style={rowStyle}>
                <span style={labelStyle}>Conversion name</span>
                <Input value={conversionName} onChange={(v) => setConversionName(v)} placeholder="print_ready" />
              </div>
            )}

            {outputMode === 'add_new_asset' && (
              <div style={rowStyle}>
                <Checkbox
                  label="Inherit metadata from this asset"
                  checked={inheritMetadata}
                  onChange={(e) => setInheritMetadata(e.target.checked)}
                />
              </div>
            )}

            <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginBottom: 4 }}>
              <button type="button" style={toggleStyle} aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((o) => !o)}>
                {advancedOpen ? <CaretDown style={caretStyle} /> : <CaretRight style={caretStyle} />}
                Advanced options
              </button>
              <p style={defaultsNoteStyle}>
                Defaults are tuned for the best quality and broadest printer compatibility — CMYK TIFF (lossless),
                US Web Coated (SWOP), relative colorimetric, 300&nbsp;DPI, original size. You can change the defaults if your print provider requires specific settings.
              </p>
            </div>

            {advancedOpen && (
              <>
                <div style={rowStyle}>
                  <span style={labelStyle}>Format</span>
                  <span style={hintStyle}>
                    Output file type. TIFF (lossless) is the recommended prepress default; JPEG is smaller; PDF /
                    PDF&#8209;X produce print-ready documents.
                  </span>
                  <Select value={format} onChange={(v) => setFormat(v)} options={FORMAT_OPTIONS} />
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>Target size</span>
                  <span style={hintStyle}>Resize to a standard page size, or keep the original dimensions.</span>
                  <Select
                    value={targetSize}
                    onChange={(v) => {
                      setTargetSize(v);
                      if (!v) setAllowUpscale(false);
                    }}
                    options={TARGET_SIZE_OPTIONS}
                  />
                </div>

                {targetSize && (
                  <div style={rowStyle}>
                    <Checkbox
                      label="Allow AI upscaling if the source is too small (uses credits)"
                      checked={allowUpscale}
                      onChange={(e) => setAllowUpscale(e.target.checked)}
                    />
                  </div>
                )}

                <div style={rowStyle}>
                  <span style={labelStyle}>DPI</span>
                  <span style={hintStyle}>Print resolution. 300&nbsp;DPI is the standard for high-quality print.</span>
                  <Input type="number" value={dpi} onChange={(v) => setDpi(v)} placeholder="300" />
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>CMYK profile</span>
                  <span style={hintStyle}>
                    Target press colour space. US Web Coated (SWOP) is the most widely supported — recommended unless
                    your printer specifies another.
                  </span>
                  <Select value={cmykProfile} onChange={(v) => setCmykProfile(v)} options={CMYK_PROFILE_OPTIONS} />
                </div>

                <div style={rowStyle}>
                  <span style={labelStyle}>Rendering intent</span>
                  <span style={hintStyle}>
                    How out-of-gamut colours are mapped to CMYK. Relative colorimetric (recommended) keeps accurate
                    in-gamut colours; Perceptual suits vivid photos.
                  </span>
                  <Select
                    value={renderingIntent}
                    onChange={(v) => setRenderingIntent(v)}
                    options={RENDERING_INTENT_OPTIONS}
                  />
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <Button mode="tertiary" size="small" onClick={() => closeModal()} disabled={submitting}>
                Cancel
              </Button>
              <Button mode="primary" size="small" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <Loader size="small" /> : primaryLabel}
              </Button>
            </div>

            {submitting && (
              <p style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
                {formatMessage(messages.printExportProcessing)}
              </p>
            )}
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default PrintExportDialog;
